import { isEmail } from '@tajawal/web-core-sdk-v2/lib/utils/validation.utils';
import isLength from 'validator/lib/isLength';
import matches from 'validator/lib/matches';
import { isValid, isBefore, format, subYears, isAfter } from 'date-fns';
import _includes from 'lodash/includes';
import { getNameSegments, getTrimmedPhoneNumber } from '@/utils/compute.utils';
import { OTHER_FORMAT_DIAL_CODES, SPECIAL_FORMAT_DIAL_CODES } from '@/constants/common.constants';
import { TRAVELLERS_TYPES_CODES_MAP } from '@tajawal/web-flights-sdk/lib/constants';
import { PaxType } from '@tajawal/web-flights-sdk/lib/types/common.types';
import { TFunction } from 'i18next';
import { TravellerModel } from '@tajawal/web-flights-sdk/lib/types/traveller.types';

// TODO - Move to logic to SDK

export function validateContactFullName(value: string, t: TFunction): string {
  const [firstName, lastName] = getNameSegments(value);
  if (!value) {
    return t('Pax.ContactValidation_EnterContactName');
  }
  if (isLength(firstName, { max: 1 }) || isLength(lastName, { max: 1 })) {
    return t('Pax.ContactValidation_ShortCN');
  }
  if (isLength(firstName, { min: 21 }) || isLength(lastName, { min: 21 })) {
    return t('Pax.ContactValidation_LongCN');
  }
  if (!matches(value, /^[a-zA-Z\s]*$/)) {
    return t('Pax.ContactValidation_EnOnlyCN');
  }
}

export function validateContactLastName(value: string, t: TFunction): string {
  if (!value) {
    return t('Pax.ContactValidation_EnterLN');
  }
  if (isLength(value, { max: 2 })) {
    return t('Pax.ContactValidation_ShortLN');
  }
  if (isLength(value, { min: 21 })) {
    return t('Pax.ContactValidation_LongLN');
  }
  if (!matches(value, /^[a-zA-Z\s]*$/)) {
    return t('Pax.ContactValidation_EnOnlyLN');
  }
}

export function validateContactEmail(value: string, t: TFunction): string {
  if (!value) {
    return t('Pax.ContactValidation_EmailMissing');
  }
  if (!isEmail(value)) {
    return t('Pax.ContactValidation_InvalidEmail');
  }
}

export function validateDialCode(value: string | number, t: TFunction): string {
  if (!value) {
    return t('Pax.ContactValidation_InvalidPhone');
  }
}

export function validateContactPhoneNumber(value: string, dialCode: string, t: TFunction): string {
  const trimmedValue = getTrimmedPhoneNumber(dialCode, value);
  if (!trimmedValue) {
    return t('Pax.ContactValidation_AddPhone');
  }
  if (!matches(trimmedValue, /^[0-9]*$/)) {
    return t('Pax.ContactValidation_OnlyNumbers');
  }

  if (_includes(OTHER_FORMAT_DIAL_CODES, dialCode) && trimmedValue.length > 10) {
    return t('Pax.ContactValidation_InvalidPhone');
  }

  if (_includes(SPECIAL_FORMAT_DIAL_CODES, dialCode) && isLength(trimmedValue, { max: 8 })) {
    return t('Pax.ContactValidation_InvalidPhone');
  }
  if (isLength(trimmedValue, { max: 4 })) {
    return t('Pax.ContactValidation_PhoneMin');
  }
  if (isLength(trimmedValue, { min: 14 })) {
    return t('Pax.ContactValidation_PhoneMax');
  }
}

export function validateTravellerTitle(value: string, t) {
  if (!value) {
    return t('Pax.TravellerFormValidation_SelectTitle');
  }
}

export function validateTravellerFirstName(value: string, t) {
  if (!value) {
    return t('Pax.TravellerFormValidation_EnterFN');
  }
  if (isLength(value, { max: 2 })) {
    return t('Pax.TravellerFormValidation_ShortFN');
  }
  if (isLength(value, { min: 21 })) {
    return t('Pax.TravellerFormValidation_LongFN');
  }
  if (!matches(value, /^[a-zA-Z\s]*$/)) {
    return t('Pax.TravellerFormValidation_EnOnlyFN');
  }
}

export function validateTravellerMiddleName(value: string, t) {
  if (!value) return;

  if (isLength(value, { max: 2 })) {
    return t('Pax.TravellerFormValidation_ShortMN');
  }
  if (isLength(value, { min: 21 })) {
    return t('Pax.TravellerFormValidation_LongMN');
  }
  if (!matches(value, /^[a-zA-Z\s]*$/)) {
    return t('Pax.TravellerFormValidation_EnOnlyMN');
  }
}

export function validateTravellerLastName(value: string, t) {
  if (!value) {
    return t('Pax.TravellerFormValidation_EnterLN');
  }
  if (isLength(value, { max: 2 })) {
    return t('Pax.TravellerFormValidation_ShortLN');
  }
  if (isLength(value, { min: 21 })) {
    return t('Pax.TravellerFormValidation_LongLN');
  }
  if (!matches(value, /^[a-zA-Z\s]*$/)) {
    return t('Pax.TravellerFormValidation_EnOnlyLN');
  }
}

export function validateTravellerNationality(value: string, t) {
  if (!value) {
    return t('Pax.TravellerFormValidation_NationalityMissing');
  }
}

export function validateTravellerDateField(value: string | number, t: TFunction): string {
  if (!value || (typeof value !== 'string' && typeof value !== 'number')) {
    return t('PaxForm.PaxFormValidation_BirthDateMissing');
  }
}

export function validateTravellerDateOfBirth({
  value,
  travellerType,
  startDate,
  t,
}: {
  value: Date;
  travellerType: PaxType;
  startDate: Date;
  t: TFunction;
}): string {
  const { ADT, CH, INF } = TRAVELLERS_TYPES_CODES_MAP;

  if (!isValid(value)) {
    return t('Pax.TravellerFormValidation_BirthDateMissing');
  }

  switch (travellerType) {
    case ADT:
      // @ts-ignore remove once date-fns is migrated to v2
      if (isAfter(value, subYears(startDate, 12))) {
        return t('Pax.TravellerFormValidation_AdultValidationError');
      }
      break;

    case CH:
      // @ts-ignore remove once date-fns is migrated to v2
      if (isAfter(value, subYears(startDate, 2))) {
        return t('Pax.TravellerFormValidation_ChildValidationError');
      }
      // @ts-ignore remove once date-fns is migrated to v2
      if (isBefore(value, subYears(startDate, 12))) {
        return t('Pax.TravellerFormValidation_ChildValidationError');
      }
      break;

    case INF:
      // @ts-ignore remove once date-fns is migrated to v2
      if (isAfter(value, startDate)) {
        return t('Pax.TravellerFormValidation_InfantValidationError');
      }
      // @ts-ignore remove once date-fns is migrated to v2
      if (isBefore(value, subYears(startDate, 2))) {
        return t('Pax.TravellerFormValidation_InfantValidationError');
      }
      break;

    default:
  }
}

export function validateTravellerIdType(value: string, t) {
  if (!value) {
    return t('Pax.TravellerFormValidation_IdTypeMissing');
  }
}

export function validateTravellerIssuingCountry(value: string, t) {
  if (!value) {
    return t('Pax.TravellerFormValidation_IssuingCountryMissing');
  }
}

export function validateTravellerIdNumber(value: string, type = 'passport', t) {
  let documentType = t('Pax.TravellerForm_Passport');
  switch (type) {
    case 'nationalId':
      documentType = t('Pax.TravellerForm_GCCID');
      break;
    case 'iqamaId':
      documentType = t('Pax.TravellerForm_iqamaID');
      break;
    default:
      break;
  }

  if (!value) {
    return t('Pax.TravellerFormValidation_IdNumberMissing', { documentType });
  }

  if (value && !matches(value, /^[a-zA-Z0-9]*$/)) {
    return t('Pax.TravellerFormValidation_IdNumberInvalid', { documentType });
  }
}

export function validateTravellerIdExpiryDate({
  value,
  startDate,
  t,
}: {
  value: Date;
  startDate: Date;
  t: TFunction;
}): string {
  if (!value) return;
  if (!isValid(value)) {
    return t('Pax.TravellerFormValidation_IdExpiryDateMissing');
  }
  // @ts-ignore remove once date-fns is migrated to v2
  if (isBefore(value, startDate)) {
    return `${t(`Pax.TravellerFormValidation_IdExpiryDateInvalid`)} ${format(startDate, 'dd/MM/yyyy')}`;
  }
}

export function validateFrequentFlyerNumber(value: string, t: TFunction): string {
  if (!value) {
    return t('Pax.FrequentFlyerValidation_EnterFrequentFlyerNumber');
  }
}

export function validatePaxButton(value: boolean, t: TFunction): string {
  if (!value) {
    return t('Pax.TravellerFormValidation_TravellerDetailsRequired');
  }
}

export function getRequiredFields(traveller: TravellerModel): string[] {
  const { isDateOfBirthRequired, isAPIsRequired, isIDRequired } = traveller || {};
  const isNationalityRequired = isAPIsRequired || isIDRequired;

  const requiredFields = ['title', 'firstName', 'lastName', 'isValueSavedOnce'];

  isDateOfBirthRequired && requiredFields.push('birthDate');
  isNationalityRequired && requiredFields.push('nationality');
  isIDRequired && requiredFields.push('idType', 'idNumber', 'issuingCountry', 'idExpiryDate');

  return requiredFields;
}
